import React, { useEffect } from 'react';
import Chart, { ReactGoogleChartProps } from 'react-google-charts';
import $ from 'jquery';

import { useTranslation } from 'react-i18next';
import { ChartContainer, Icon } from './styles';

type ChartsProps = ReactGoogleChartProps & {
  size?: 'small' | 'medium' | 'large';
  hAxisTitle?: string;
};

function calcularMaiorValor(data: (string | number | object)[][]): number {
  return Math.max(...data.slice(1).map(row => row[1] as number));
}

export function ChartComponent({ chartType, data, options, diffdata, size, hAxisTitle }: ChartsProps) {

  const { i18n } = useTranslation();
  const pallete = [
    '#f14c14',
    '#f39c35',
    '#68BC00',
    '#1d7b63',
    '#4e97a8',
    '#4466a3',
    '#00bfff',
    '#f4a460',
    '#b22222',
    '#06C',
    '#5752D1',
    '#009596',
    '#EF9234',
    '#2A265F',
    '#F0AB00',
    '#A30000',
    '#EF9234',
    '#EC7A08',
    '#23511E',
    '#470000',
    '#F9E0A2',
    '#003737',
    '#8F4700',
  ];

  useEffect(() => {
    const observer = new MutationObserver(() => {
      $('span').each(function () {
        const $el = $(this);
        const text = $el.text().trim();

        if (text === 't') {
          const $next = $el.next('span');
          if ($next.text().trim() === '100') {
            $el.text(' Total ');
          }
        }
      });
      $('text').each(function () {
        if ($(this).text() === 'Previous data') {
          if (i18n.language === 'en-US') {
            $(this).text('Total percentage');
          } else if (i18n.language === 'es-ES') {
            $(this).text('Porcentaje total');
          } else {
            $(this).text('Percentual total');
          }
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [i18n.language]);


  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let newData: any[] = [];
  let maiorValor = 0;
  if (data && Array.isArray(data)) {
    newData = data;
    maiorValor = calcularMaiorValor(data);
  }

  let hasCustomLegenda = false;
  if (options) {
    options.tooltip = { isHtml: true };
    options.annotations = { alwaysOutside: true, textStyle: { bold: true } };

    if (options.vAxis) {
      options.vAxis.titleTextStyle = { fontSize: 12 };

      if (chartType !== 'BarChart') {
        options.vAxis.viewWindow = {
          max: maiorValor + maiorValor * 0.2,
        };
      }
    }

    if (options.seriesType === 'line') {
      options.chartArea = { left: 20, top: 10, bottom: 50, width: '100%', height: '100%' };
    } else if (chartType === 'PieChart') {
      options.chartArea = { left: 10, top: 10, bottom: 10, right: 10, width: '100%', height: '100%' };
    } else if (chartType !== 'BarChart' && options.seriesType === 'bars') {
      options.chartArea = { left: 20, top: 10, bottom: 10, width: '100%', height: '100%' };
    }

    if ((options.seriesType === 'bars' && chartType != 'BarChart') || chartType === 'PieChart') {
      hasCustomLegenda = true;
      options.colors = pallete;

      newData = newData.map((item, index) => {
        if (index === 0) {
          if (item[2]) {
            return [item[0], item[1], item[2], { role: 'style' }, { role: 'tooltip' }];
          } else {
            return [item[0], item[1], { role: 'style' }, { role: 'tooltip' }];
          }
        }

        if (item[2]) {
          return [item[0], item[1], item[2], pallete[index - 1], `${item[0]}: ${formatarNumeroBrasileiro(item[1])}`];
        } else {
          return [item[0], item[1], pallete[index - 1], `${item[0]}: ${formatarNumeroBrasileiro(item[1])}`];
        }
      });
    }
  }

  function formatarNumeroBrasileiro(numero: number): string {
    const formatter = new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

    return formatter.format(numero);
  }

  return (
    <ChartContainer size={size}>
      {diffdata ? (
        <Chart
          chartType={chartType}
          width="100%"
          height="100%"
          diffdata={diffdata}
          options={options}
          chartLanguage={i18n.language}
          chartEvents={[
            {
              eventName: "ready",
              callback: ({ chartWrapper }) => {
                const container = document.getElementById(chartWrapper.getContainerId());
                const svg = container?.querySelector("svg");
                const rect = svg?.querySelectorAll("rect");
                const g = svg?.querySelectorAll("g");

                rect?.forEach((i: any) => {
                  if (i.logicalname?.includes("bar#") && Number(i.logicalname?.slice(-1)) % 2 === 0) {
                    i.style.pointerEvents = "none";
                  }
                })

                g?.forEach((i: any) => {
                  if (i.logicalname?.includes("legend")) {
                     i.style.pointerEvents = "none";
                  }
                })
              },
            },
          ]}
        />
      ) : (
        <Chart
          chartType={chartType}
          width="100%"
          height="100%"
          data={newData}
          options={options}
          chartLanguage={i18n.language}
        />
      )}

      {hAxisTitle && (
        <div className="hAxis">
          <span>{hAxisTitle}</span>
        </div>
      )}

      {hasCustomLegenda && (
        <div className="legenda">
          <h3>Legenda</h3>
          <ul className="lista">
            {newData.map((item, index) => {
              if (index === 0) return;

              return (
                <li key={index - 1} className="elemento">
                  <Icon color={pallete[index - 1]} />
                  <span>{item[0]}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </ChartContainer>
  );
}
